import {
  FilterBuilder,
  Tooltip,
  useOptions,
} from '@cotera/client/app/components';
import { useWatchableValue } from '@cotera/client/v0/actions/framework/watchable';
import { Assert } from '@cotera/utilities';
import { LLMColumnFilterPreviewAction } from '../actions/llm-column-preview.action';
import { useColumnModel } from '../context';
import { useAction } from '@cotera/client/v0/actions/framework';
import { GraphDataGrid } from '@cotera/client/v0/data-grid';
import { Column } from '@cotera/client/v0/components/data-grid/types';
import React, { useEffect, useMemo } from 'react';
import { Button } from '@cotera/client/v0/components/ui/button';
import { usePreviewCellRenderer } from './preview-cell-renderer';
import { DG } from '@cotera/sdk/core';
import { RunAllPreviewsAction } from '../actions/run-all-previews.action';

const Filter: React.FC<{}> = () => {
  const model = useColumnModel();
  const datasetRel = useWatchableValue(model.datasetRel);

  Assert.assert(
    datasetRel !== null && datasetRel.isOk(),
    'Dataset rel not found'
  );
  Assert.assert(datasetRel.value.t === 'rel');

  const rel = datasetRel?.value.rel;
  const options = useOptions(rel.attributes);
  const filters = useWatchableValue(model.filterGroup);

  return (
    <FilterBuilder
      dangerouslyOptInToDynamicOptions
      filterGroup={filters}
      options={options}
      autoRun={false}
      showRun={false}
      rel={rel}
      runEnabled
      runVariants={[
        {
          label: 'Refresh Data',
          id: 'source',
          icon: 'filter',
        },
      ]}
      onChange={({ filterGroup }) => {
        model.filterGroup.set(filterGroup);
      }}
      onRun={() => {
        //run is handled by the model
      }}
    />
  );
};

export const FilterSection: React.FC = () => {
  const model = useColumnModel();
  const runAction = useAction(LLMColumnFilterPreviewAction);
  const datasetRel = useWatchableValue(model.datasetRel);

  return (
    <div className="flex items-center justify-between pb-4 border-b border-divider">
      {datasetRel?.isOk() ? <Filter /> : null}
      <Button
        icon="filter"
        variant="primary"
        size={'md'}
        onClick={() => runAction.execute()}
        className="ml-2"
      >
        Apply
      </Button>
    </div>
  );
};

export const DatasetPreview: React.FC = () => {
  const model = useColumnModel();
  const rel = useWatchableValue(model.preview);
  const currentColumnName = useWatchableValue(model.currentNameInGraph);
  const dependencies = useWatchableValue(model.dependencies);
  const hasBeenTargeted = useWatchableValue(model.hasBeenTargeted);

  // Reset shimmer state when preview data is available (e.g. after Refresh Data or run completes)
  useEffect(() => {
    if (rel !== null) {
      model.runOnNewRowsClicked.set(false);
    }
  }, [model, rel]);

  const getEditorState = useMemo(() => {
    return (_columnName: string) => {
      // Always use currentColumnName since this preview is for a specific column
      if (model.version.snapshot() === 'v1') {
        const existingConfig = model.config.snapshot() as DG.Graph.LlmColConfig;
        const formState = model.formState.snapshot();

        // Merge formState with existing config (formState takes precedence)
        const mergedConfig: DG.Graph.LlmColConfig = {
          ...existingConfig,
          systemMessage: formState.systemMessage,
          prompt: formState.prompt,
          model: formState.model,
          temperature: formState.temperature,
          maxOutputTokens: formState.maxOutputTokens,
          when: formState.when,
          imageUrl: formState.imageUrl,
          tools: formState.tools,
        };

        return DG.DocumentEditor.EditorState.fromV1({
          columnName: currentColumnName,
          llmConfig: mergedConfig,
        }).getState();
      }
      // For v2, build from the current v2EditorState (which is updated from formState)
      return model.v2EditorState.snapshot();
    };
  }, [model, currentColumnName]);

  const getLlmConfig = useMemo(() => {
    return (_columnName: string) => {
      // Always use currentColumnName since this preview is for a specific column
      const formState = model.formState.snapshot();
      return {
        model: formState.model,
        schedule: formState.when,
        temperature: formState.temperature,
        maxOutputTokens: formState.maxOutputTokens,
      };
    };
  }, [model]);

  const PreviewCellRenderer = usePreviewCellRenderer({
    getEditorState,
    getLlmConfig,
    atcColumns: [currentColumnName],
    relName: model.datasetName,
    isPreviewMode: true,
  });

  const columnConfigOverride = useMemo(() => {
    if (!currentColumnName) {
      return undefined;
    }
    return (column: Column) => {
      // Hide all columns except the current one
      if (
        column.key !== currentColumnName &&
        !dependencies.includes(column.key)
      ) {
        return {
          status: 'hidden' as const,
          inView: false,
        };
      }

      return {
        status: 'visible' as const,
        inView: true,
      };
    };
  }, [currentColumnName, dependencies]);

  if (rel === null) {
    return <FilterSection />;
  }

  return (
    <div className="flex flex-col w-full h-full">
      <FilterSection />
      <GraphDataGrid
        showPkUniquenessWarning={false}
        rel={rel}
        storageKey={`${model.datasetName}-${currentColumnName}-preview`}
        managedProps={model}
        relName={model.datasetName}
        buildExplorer={() => model.inputs.graph.snapshot()}
        shouldShow={hasBeenTargeted}
        AtcCellComponent={PreviewCellRenderer}
        columnConfigOverride={columnConfigOverride}
        headerPlayButton={() => <HeaderPlayButton />}
      />
    </div>
  );
};

const HeaderPlayButton: React.FC = () => {
  const runAction = useAction(RunAllPreviewsAction);
  return (
    <Tooltip side="top" tooltipContent="Run All Previews">
      <Button
        icon="play"
        variant="outline"
        size={'icon-sm'}
        onClick={async () => {
          await runAction.execute();
        }}
      />
    </Tooltip>
  );
};
